import React, { useEffect, useMemo, useState } from 'react';
import { getStoredMe } from '../traccar';
import { getCachedShop, fetchShop, placeOrder, orderTotal, startCheckout, verifyPayment, markPaid } from '../shopStore';
import AddSharedVehicle from '../AddSharedVehicle';

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Customer-facing shop. Browse the devices the admin sells and place an order
// right from the app — no phone call. Orders land in the admin Orders tab.
export default function ShopPage({ onOrdered } = {}) {
  const me = getStoredMe() || {};
  const [products, setProducts] = useState(() => getCachedShop().products);
  const [cart, setCart] = useState({}); // productId -> qty
  const [ship, setShip] = useState({
    name: me.name || '', phone: (me.attributes && me.attributes.phone) || '',
    address: '', city: '', state: '', zip: '',
  });
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null); // the confirmed order
  const [err, setErr] = useState('');

  useEffect(() => { fetchShop().then((s) => setProducts(s.products)).catch(() => {}); }, []);

  // Returning from Stripe Checkout? Confirm the payment and mark the order paid.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('paid') === '1' && q.get('session_id')) {
      (async () => {
        const v = await verifyPayment(q.get('session_id'));
        const orderId = q.get('order');
        if (v.paid && orderId) { try { await markPaid(orderId, q.get('session_id')); } catch { /* ignore */ } }
        setPlaced({ id: orderId || 'paid', paid: !!v.paid, amount: v.amount });
        window.history.replaceState({}, '', window.location.pathname); // clean the URL
      })();
    }
  }, []);

  const active = products.filter((p) => p.active !== false);
  const items = Object.entries(cart).filter(([, q]) => q > 0).map(([productId, qty]) => ({ productId, qty }));
  const total = useMemo(() => orderTotal({ items }, products), [items, products]);
  const setQty = (id, q) => setCart((c) => ({ ...c, [id]: Math.max(0, q) }));

  const submit = async () => {
    setErr('');
    if (items.length === 0) { setErr('Add at least one device to your order.'); return; }
    if (!ship.name || !ship.address || !ship.city || !ship.state || !ship.zip) { setErr('Please complete your shipping address.'); return; }
    setBusy(true);
    try {
      const order = await placeOrder({
        customerId: me.id, account: (me.attributes && me.attributes.account) || '',
        customerName: ship.name, email: me.email || '', phone: ship.phone,
        address: ship.address, city: ship.city, state: ship.state, zip: ship.zip,
        items,
      });
      onOrdered && onOrdered(order);
      // Try to collect payment now via Stripe; if payments aren't set up, just
      // confirm the order (admin fulfills + collects payment separately).
      try {
        const url = await startCheckout(items, order.id);
        if (url) { window.location.href = url; return; }
        setPlaced(order);
      } catch (pe) {
        if (pe.code && pe.code !== 'not-configured') setErr(`Payment couldn’t start (${pe.message}). Your order was placed — we’ll follow up.`);
        setPlaced(order);
      }
    } catch (e) { setErr(e.message || 'Could not place the order. Please try again.'); }
    finally { setBusy(false); }
  };

  if (placed) {
    const amount = placed.amount != null ? placed.amount : total;
    return (
      <div style={wrap}>
        <div style={{ ...card, textAlign: 'center', maxWidth: '520px', margin: '40px auto' }}>
          <div style={{ fontSize: '46px' }}>{placed.paid ? '🎉' : '✅'}</div>
          <h1 style={{ fontSize: '22px', color: '#0f172a', margin: '8px 0' }}>{placed.paid ? 'Payment received!' : 'Order placed!'}</h1>
          <p style={{ color: '#475569', lineHeight: 1.6 }}>
            Thanks, {(ship.name || '').split(' ')[0] || 'there'}. Your order <b>#{String(placed.id).slice(-6)}</b>{amount ? ` for ${money(amount)}` : ''} is in{placed.paid ? ' and paid' : ''}.
            We’ll provision your device and ship it to your address. You’ll be able to track everything here as soon as it’s active.
          </p>
          <button onClick={() => { setPlaced(null); setCart({}); }} style={{ ...btn, marginTop: '10px' }}>Back to shop</button>
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <h1 style={{ fontSize: '24px', color: '#0f172a', margin: '0 0 4px', fontFamily: 'Poppins, sans-serif' }}>🛒 Shop devices</h1>
      <p style={{ color: '#64748b', margin: '0 0 16px' }}>Pick a tracker, place your order, and we’ll get you set up — no phone call needed.</p>

      {/* Already have a device? Add it with a share code instead of buying. */}
      <div style={{ ...card, background: 'linear-gradient(135deg,#f0fdf4,#ecfdf5)', border: '1px solid #bbf7d0', marginBottom: '22px' }}>
        <div style={{ fontWeight: 800, fontSize: '15px', color: '#15803d', marginBottom: '2px' }}>Already have a device?</div>
        <div style={{ fontSize: '12.5px', color: '#166534', marginBottom: '10px' }}>Add a car someone shared with you — enter their share code and they’ll approve it.</div>
        <AddSharedVehicle onAdded={onOrdered} buttonLabel="➕ Add a device with a share code" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {active.length === 0 && <div style={{ color: '#94a3b8' }}>No devices are available right now — check back soon.</div>}
        {active.map((p) => {
          const qty = cart[p.id] || 0;
          return (
            <div key={p.id} style={{ ...card, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                <h2 style={{ fontSize: '17px', color: '#0f172a', margin: 0 }}>{p.name}</h2>
                <span style={{ fontSize: '18px', fontWeight: 800, color: '#0066ff', whiteSpace: 'nowrap' }}>{money(p.price)}</span>
              </div>
              {p.description && <p style={{ color: '#475569', fontSize: '13px', lineHeight: 1.5, margin: '8px 0' }}>{p.description}</p>}
              {Array.isArray(p.perks) && p.perks.length > 0 && (
                <ul style={{ margin: '4px 0 12px', paddingLeft: '18px', color: '#334155', fontSize: '13px', lineHeight: 1.6 }}>
                  {p.perks.map((perk, i) => <li key={i}>{perk}</li>)}
                </ul>
              )}
              <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                {qty === 0 ? (
                  <button onClick={() => setQty(p.id, 1)} style={{ ...btn, flex: 1 }}>Add to order</button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button onClick={() => setQty(p.id, qty - 1)} style={qtyBtn}>−</button>
                    <span style={{ fontWeight: 700, minWidth: '20px', textAlign: 'center' }}>{qty}</span>
                    <button onClick={() => setQty(p.id, qty + 1)} style={qtyBtn}>+</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {items.length > 0 && (
        <div style={{ ...card, maxWidth: '560px' }}>
          <h2 style={{ fontSize: '16px', color: '#0f172a', margin: '0 0 12px' }}>Ship to</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <input style={inp} placeholder="Full name" value={ship.name} onChange={(e) => setShip({ ...ship, name: e.target.value })} />
            <input style={inp} placeholder="Phone" value={ship.phone} onChange={(e) => setShip({ ...ship, phone: e.target.value })} />
            <input style={{ ...inp, gridColumn: '1 / -1' }} placeholder="Street address" value={ship.address} onChange={(e) => setShip({ ...ship, address: e.target.value })} />
            <input style={inp} placeholder="City" value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <input style={{ ...inp, width: '80px' }} placeholder="State" value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value })} />
              <input style={{ ...inp, flex: 1 }} placeholder="ZIP" value={ship.zip} onChange={(e) => setShip({ ...ship, zip: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #eef2f7' }}>
            <div><span style={{ color: '#64748b', fontSize: '13px' }}>Order total</span><div style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>{money(total)}</div></div>
            <button onClick={submit} disabled={busy} style={{ ...btn, background: busy ? '#94a3b8' : btn.background, padding: '13px 22px', fontSize: '15px' }}>
              {busy ? 'Placing…' : 'Place order'}
            </button>
          </div>
          {err && <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '8px' }}>{err}</div>}
          <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '8px' }}>You won’t be charged here — we’ll confirm payment and shipping after you order.</div>
        </div>
      )}
      {items.length === 0 && err && <div style={{ color: '#dc2626', fontSize: '13px' }}>{err}</div>}
    </div>
  );
}

const wrap = { padding: '28px 26px 80px', maxWidth: '1100px' };
const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: '14px', padding: '18px', boxShadow: '0 2px 10px rgba(15,23,42,0.04)' };
const btn = { background: 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 700, border: 'none', borderRadius: '10px', padding: '10px 16px', cursor: 'pointer', fontSize: '14px' };
const qtyBtn = { width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', fontSize: '18px', cursor: 'pointer', color: '#0f172a' };
const inp = { border: '1px solid #cbd5e1', borderRadius: '8px', padding: '9px 11px', fontSize: '14px', width: '100%', boxSizing: 'border-box' };
